// TeacherDayPlannerSelf.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import API from './api';

/** ---------- Time utils ---------- */
function normalizeTime(val) {
  if (!val) return '';
  let t = String(val);
  if (t.includes('AM') || t.includes('PM')) {
    const [hm, part] = t.split(' ');
    let [h, m] = hm.split(':').map(Number);
    if (part === 'PM' && h < 12) h += 12;
    if (part === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }
  return t.slice(0, 5);
}
function toMinutes(t) {
  if (!t) return 0;
  const [h, m] = normalizeTime(t).split(':').map(Number);
  return h * 60 + m;
}
function minutesToHHMM(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}`;
}
function addMinutes(time, mins) { return minutesToHHMM(toMinutes(time) + mins); }
function clampTo(i, startM, endM) {
  const f = Math.max(toMinutes(i.from), startM);
  const t = Math.min(toMinutes(i.to), endM);
  if (t <= f) return null;
  return { from: minutesToHHMM(f), to: minutesToHHMM(t) };
}
function simplifyIntervals(list = []) {
  return (list || []).map(x => ({
    from: normalizeTime(x.from_time ?? x.from),
    to: normalizeTime(x.to_time ?? x.to),
  })).filter(i => toMinutes(i.to) > toMinutes(i.from));
}
function mergeTouching(intervals = []) {
  const sorted = intervals.slice().sort((x,y)=>toMinutes(x.from)-toMinutes(y.from));
  const out = [];
  for (const it of sorted) {
    if (!out.length) out.push({...it});
    else {
      const last = out[out.length-1];
      if (toMinutes(it.from) <= toMinutes(last.to)) {
        if (toMinutes(it.to) > toMinutes(last.to)) last.to = it.to;
      } else out.push({...it});
    }
  }
  return out;
}
function subtractIntervals(base = [], blocked = []) {
  let result = simplifyIntervals(base);
  const bad = simplifyIntervals(blocked);
  for (const exc of bad) {
    const excFrom = toMinutes(exc.from), excTo = toMinutes(exc.to);
    const next = [];
    for (const it of result) {
      const a = toMinutes(it.from), b = toMinutes(it.to);
      if (excTo <= a || excFrom >= b) next.push(it);
      else {
        if (excFrom > a) next.push({ from: it.from, to: normalizeTime(exc.from) });
        if (excTo   < b) next.push({ from: normalizeTime(exc.to), to: it.to });
      }
    }
    result = next;
  }
  return result;
}
function intersectTwoSets(A = [], B = []) {
  const res = [];
  const a = simplifyIntervals(A);
  const b = simplifyIntervals(B);
  for (const i of a) {
    for (const j of b) {
      const from = Math.max(toMinutes(i.from), toMinutes(j.from));
      const to   = Math.min(toMinutes(i.to),   toMinutes(j.to));
      if (to > from) res.push({ from: minutesToHHMM(from), to: minutesToHHMM(to) });
    }
  }
  return mergeTouching(res);
}

/** ---------- Timeline config ---------- */
const SNAP_MIN  = 5;
const SLOT_MIN  = 45; // standard = 45 min
const MAX_STANDARD_MIN = 45;

/** ---------- UI helpers ---------- */
function ReassignedBadge({ value }) {
  if (!value || value === 'none') return null;
  const cls = {
    rejected: 'bg-red-100 text-red-700',
    pending:  'bg-blue-100 text-blue-700',
    accepted: 'bg-green-100 text-green-800',
  }[value] || 'bg-gray-100 text-gray-700';
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{value}</span>;
}

const COLOR_PALETTE = [
  'bg-emerald-400', 'bg-sky-400', 'bg-fuchsia-400',
  'bg-amber-400', 'bg-lime-400', 'bg-rose-400',
  'bg-cyan-400', 'bg-violet-400', 'bg-orange-400'
];
function colorForId(id) {
  if (!id && id !== 0) return 'bg-gray-400';
  const idx = Math.abs(parseInt(id, 10)) % COLOR_PALETTE.length;
  return COLOR_PALETTE[idx];
}
function initials(name='') {
  const parts = name.trim().split(/\s+/);
  return parts.slice(0,2).map(p=>p[0]?.toUpperCase()||'').join('');
}
function TypeBadge({ type }) {
  const isEvent = type === 'event';
  const cls = isEvent ? 'bg-yellow-100 text-orange-800' : 'bg-green-100 text-green-800';
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{isEvent ? 'Event' : 'Standard'}</span>;
}
function StatusBadge({ status }) {
  const map = {
    draft: 'bg-blue-100 text-blue-700',
    complete: 'bg-green-200 text-green-900',
    canceled: 'bg-purple-200 text-purple-700',
  };
  const cls = map[status] || 'bg-gray-200 text-gray-700';
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{status || '—'}</span>;
}
function Toast({ text, onClose }) {
  if (!text) return null;
  return (
    <div className="fixed top-4 right-4 z-[9999] text-white px-3 py-2 rounded shadow bg-slate-700">
      <div className="flex items-center gap-3">
        <span className="text-sm">{text}</span>
        <button onClick={onClose} className="text-white/90 hover:text-white text-lg leading-none">×</button>
      </div>
    </div>
  );
}

const fmtMi  = v => (v==null ? null : `${Number(v).toFixed(2)} mi`);
const fmtMin = v => (v==null ? null : `${Math.round(Number(v))} min`);

function DistanceLine({ item }) {
  const rmi = item?.route_mi;
  const rmn = item?.route_min;
  const sli = item?.straight_line_mi;
  const src = item?.location_source;

  const parts = [];
  if (rmi != null && rmn != null) parts.push(`${fmtMi(rmi)} (${fmtMin(rmn)})`);
  else if (rmi != null) parts.push(fmtMi(rmi));
  if (sli != null) parts.push(`straight ${fmtMi(sli)}`);
  if (src === 'alt') parts.push('ALT address');
  if (src === 'primary') parts.push('Primary address');

  if (!parts.length) return null;
  return <span className="text-xs text-gray-600">{parts.join(' • ')}</span>;
}

function PrevRefPill({ prev_ref }) {
  if (!prev_ref?.end_time) return null;
  return (
    <span className="text-[11px] text-gray-500 ml-2">
      from last stop {prev_ref.end_time}
    </span>
  );
}

/** ---------- Component za teachera ---------- */
export default function TeacherDayPlannerSelf() {
  // Ulogovani teacher
  const [currentTeacherId, setCurrentTeacherId] = useState('');
  const [currentTeacherName, setCurrentTeacherName] = useState('');

  // Global lists
  const [students, setStudents] = useState([]);

  // Selection
  const [selectedStudentId, setSelectedStudentId] = useState(''); // optional overlap hint

  // Calendar / week
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState('');
  const [weekDates, setWeekDates] = useState([]);

  // Server time
  const [serverDateStr, setServerDateStr] = useState('');
  const [serverTimeHM, setServerTimeHM] = useState('00:00');

  // Availability caches
  const [teacherAvailByDate, setTeacherAvailByDate] = useState({});
  const [teacherBusyByDate, setTeacherBusyByDate] = useState({});

  const [studentAvailCache, setStudentAvailCache] = useState({});
  const [studentVisitsCache, setStudentVisitsCache] = useState({});

  // Local candidates (unsaved)
  const [candidatesByDate, setCandidatesByDate] = useState({});

  // Modal anchor (teacher free segment)
  const [modalAnchor, setModalAnchor] = useState({ from: '', to: '' });

  // UI state
  const [status, setStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalDate, setModalDate] = useState('');
  const [modalFrom, setModalFrom] = useState('');
  const [modalMaxTo, setModalMaxTo] = useState('');
  const [isEvent, setIsEvent] = useState(false);
  const [eventEndTime, setEventEndTime] = useState('');
  const [eligibleStudents, setEligibleStudents] = useState([]); // [{student, ok, reason}]
  const [otherEligible, setOtherEligible] = useState([]);
  const [loadingOther, setLoadingOther] = useState(false);
  const fetchRef = useRef(0);

  // Distance controls
  const [distanceMode, setDistanceMode] = useState('haversine'); // 'none' | 'haversine' | 'ors'
  const [sortOthersByDistance, setSortOthersByDistance] = useState(false);

  // Drag
  const [drag, setDrag] = useState(null);
  const trackRefs = useRef({});

  // Dynamic workday
  const [workday, setWorkday] = useState({ start: '08:00', end: '18:00' });

  // Učitaj ulogovanog teachera iz localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        if (u && u.id) {
          setCurrentTeacherId(String(u.id));
          setCurrentTeacherName(u.name || '');
        }
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    API.get('/workday-settings')
      .then(res => setWorkday({
        start: (res.data?.start || '08:00').slice(0,5),
        end:   (res.data?.end   || '18:00').slice(0,5),
      }))
      .catch(()=>{});
  }, []);

  // Derived from workday
  const DAY_START = workday.start;
  const DAY_END   = workday.end;
  const dayStartM = toMinutes(DAY_START);
  const dayEndM   = toMinutes(DAY_END);
  const spanM     = Math.max(1, dayEndM - dayStartM);

  const pctBox = (i) => {
    const left = ((toMinutes(i.from) - dayStartM) / spanM) * 100;
    const width = ((toMinutes(i.to) - toMinutes(i.from)) / spanM) * 100;
    return {
      left: `${Math.max(0, left)}%`,
      width: `${Math.max(0, Math.min(100 - left, width))}%`
    };
  };

  // Toast auto-clear
  useEffect(() => {
    if (!status) return;
    const t = setTimeout(() => setStatus(''), 3000);
    return () => clearTimeout(t);
  }, [status]);

  // Initial loads (serverTime, students, weekovi)
  useEffect(() => {
    API.get('/server-time').then(res => {
      const s = res.data?.serverTime || '';
      setServerDateStr(s.slice(0,10));
      setServerTimeHM(s.slice(11,16));
    }).catch(()=>{});

    API.get('/admin/students?assigned_only=0')
      .then(res => setStudents(res.data || []))
      .catch(()=>{});

    API.get('/weeks')
      .then(res => setWeekOptions(res.data || []))
      .catch(()=>{});
  }, []);

  useEffect(() => {
    if (!selectedWeekStart) return;
    API.get(`/week-dates/${selectedWeekStart}`)
      .then(res => setWeekDates(res.data || []))
      .catch(()=>{});
  }, [selectedWeekStart]);

  // Default students za ULOGOVANOG teachera
  const defaultStudents = useMemo(() => {
    if (!currentTeacherId) return [];
    return (students || []).filter(
      s => String(s.default_teacher_id) === String(currentTeacherId)
    );
  }, [currentTeacherId, students]);

  // Reset caches na promenu teachera/week
  useEffect(() => {
    setTeacherAvailByDate({});
    setTeacherBusyByDate({});
    setCandidatesByDate({});
    setSelectedStudentId('');
  }, [currentTeacherId, selectedWeekStart]);

  // Load teacher availability + busy (za ulogovanog teachera)
  useEffect(() => {
    if (!currentTeacherId || !selectedWeekStart) return;

    (async () => {
      try {
        // 1) Teacher availability
        const res = await API.get(`/availability/week/${selectedWeekStart}?teacher_id=${currentTeacherId}`);
        const a = res.data?.availability || {};
        const e = res.data?.exceptions || {};
        const daysOff = res.data?.day_offs || [];

        const base = {};
        Object.keys(a).forEach(date => {
          const isDayOff = daysOff.includes(date);
          const free = isDayOff ? [] : subtractIntervals(a[date], e[date] || []);
          base[date] = { free };
        });

        // 2) Busy visits za ovog teachera
        const b = await API.get('/visits/teacher-week-all', {
          params: { teacher_id: currentTeacherId, week_start: selectedWeekStart }
        });
        const busy = {};
        (Object.entries(b.data || {})).forEach(([date, list]) => {
          busy[date] = (list || []).map(r => ({
            id: r.id,
            from: normalizeTime(r.from_time),
            to: normalizeTime(r.to_time),
            student_id: r.student_id,
            student_name: r.student_name,
            type: r.type || 'standard',
            status: r.status || 'draft',
            reassigned_status: r.reassigned_status || 'none'
          }));
        });

        // 3) skini zauzete slotove iz free
        Object.keys(base).forEach(date => {
          const free = base[date].free || [];
          const blocked = (busy[date] || []).map(x => ({ from: x.from, to: x.to }));
          base[date].free = subtractIntervals(free, blocked);
        });

        setTeacherBusyByDate(busy);
        setTeacherAvailByDate(base);
      } catch (e) {
        setStatus('Failed to load availability.');
      }
    })();
  }, [currentTeacherId, selectedWeekStart]);

  // Helpers
  const getStudentFull = (id) =>
    (students || []).find(s => Number(s.id) === Number(id));
  const getStudentDefaultTid = (id) =>
    getStudentFull(id)?.default_teacher_id ?? null;

  // Lazy-load student availability + visits for week
  async function ensureStudentWeek(studentId) {
    if (!studentId || !selectedWeekStart) {
      return { freeByDate: {}, visitsByDate: {} };
    }

    const cache = studentAvailCache[studentId];
    const visitsCache = studentVisitsCache[studentId];
    const hasAvail = cache && cache.__week === selectedWeekStart;
    const hasVisits = visitsCache && visitsCache.__week === selectedWeekStart;

    let freeByDate = hasAvail ? cache.freeByDate : null;
    let visitsByDate = hasVisits ? visitsCache.visitsByDate : null;

    try {
      if (!hasAvail) {
        const res = await API.get(`/student-availability/realweek/${studentId}/${selectedWeekStart}`);
        const map = {};
        Object.entries(res.data || {}).forEach(([date, obj]) => {
          const intervals = simplifyIntervals(obj.intervals || []);
          const blocked   = simplifyIntervals(obj.blocked || []);
          map[date] = subtractIntervals(intervals, blocked);
        });
        freeByDate = map;
        setStudentAvailCache(prev => ({
          ...prev,
          [studentId]: { __week: selectedWeekStart, freeByDate: map }
        }));
      }
    } catch {
      freeByDate = freeByDate || {};
    }

    try {
      if (!hasVisits) {
        const res2 = await API.get(`/visits/week/${studentId}/${selectedWeekStart}`);
        const map2 = {};
        (Object.entries(res2.data || {})).forEach(([date, list]) => {
          map2[date] = (list || []).map(s => ({
            from: normalizeTime(s.from),
            to: normalizeTime(s.type === 'event' ? (s.event_end_time || s.to) : s.to),
            visit_id: s.visit_id,
            type: s.type || 'standard'
          }));
        });
        visitsByDate = map2;
        setStudentVisitsCache(prev => ({
          ...prev,
          [studentId]: { __week: selectedWeekStart, visitsByDate: map2 }
        }));
      }
    } catch {
      visitsByDate = visitsByDate || {};
    }

    return {
      freeByDate: freeByDate || {},
      visitsByDate: visitsByDate || {}
    };
  }

  // Intersection hint (teacher & jedan default student)
  const intersectionByDate = useMemo(() => {
    if (!selectedStudentId) return {};
    const sCache = studentAvailCache[selectedStudentId];
    if (!sCache || sCache.__week !== selectedWeekStart) return {};
    const map = {};
    for (const { date } of weekDates) {
      const teacherFree = (teacherAvailByDate[date]?.free || [])
        .map(i => clampTo(i, dayStartM, dayEndM))
        .filter(Boolean);
      const studentFree = (sCache.freeByDate?.[date] || [])
        .map(i => clampTo(i, dayStartM, dayEndM))
        .filter(Boolean);
      map[date] = intersectTwoSets(teacherFree, studentFree);
    }
    return map;
  }, [selectedStudentId, teacherAvailByDate, studentAvailCache, weekDates, selectedWeekStart]);

  /** Past handling */
  function isPastDate(date) { return date < serverDateStr; }

  /** ---- Global per-day limit helper: 1 visit/day/student ---- */
  function canAddVisitForStudent(date, studentId) {
    // Lokalni kandidati za tog studenta tog dana
    const localCount = (candidatesByDate[date] || [])
      .filter(v => v.student?.id === studentId).length;

    // Ako već znamo da ima posetu tog dana (iz studentVisitsCache), blokiraj
    const vc = studentVisitsCache[studentId];
    const hasExisting =
      vc &&
      vc.__week === selectedWeekStart &&
      Array.isArray(vc.visitsByDate?.[date]) &&
      vc.visitsByDate[date].length > 0;

    return !hasExisting && localCount < 1;
  }

  /** ---------- Fetch eligibility from server & build default + others ---------- */
  async function computeEligibility({ date, from, to, type, reqId }) {
    try {
      const r = await API.post('/students/eligible-for-interval', {
        teacher_id: Number(currentTeacherId),
        date,
        from,
        to,
        type,
        limit: 500,
        distance_mode: distanceMode,
        sort_by_distance: sortOthersByDistance,
      });

      if (reqId && reqId !== fetchRef.current) return;

      const defSrv = r.data?.default_students || [];
      const othSrv = r.data?.others || [];
      const defMap = new Map(defSrv.map(s => [s.id, s]));

      // Default studenti: svi default za ovog teachera,
      // ok=true samo ako ih server vrati (1/day + ostala pravila)
      const list = [];
      for (const s of defaultStudents) {
        const serverHit = defMap.get(s.id);
        let ok = !!serverHit;
        let reason = '';
        let enriched = { ...s };

        if (serverHit) {
          enriched = {
            ...enriched,
            straight_line_mi: serverHit.straight_line_mi ?? null,
            route_mi: serverHit.route_mi ?? null,
            route_min: serverHit.route_min ?? null,
            prev_ref: serverHit.prev_ref ?? null,
            location_source: serverHit.location_source ?? 'primary'
          };
        } else {
          // pokušaćemo da objasnimo "zašto ne"
          try {
            const { freeByDate, visitsByDate } = await ensureStudentWeek(s.id);
            const free = freeByDate[date] || [];
            const hasCover = free.some(x =>
              toMinutes(from) >= toMinutes(x.from) &&
              toMinutes(to)   <= toMinutes(x.to)
            );
            const visits = visitsByDate[date] || [];
            const hasVisitThatDay = visits.length > 0;

            if (hasVisitThatDay) {
              reason = 'Already has a visit on this day.';
            } else if (!hasCover) {
              reason = 'Not available in this interval.';
            } else {
              reason = 'Not eligible for this slot (server rules).';
            }
          } catch {
            reason = 'Not eligible for this slot.';
          }
        }

        list.push({ student: enriched, ok, reason });
      }

      // sort: prvo eligible, pa unavailable
      list.sort((a,b) => (a.ok === b.ok) ? 0 : a.ok ? -1 : 1);
      if (!reqId || reqId === fetchRef.current) {
        setEligibleStudents(list);
      }

      // Others: direktno sa servera
      const enrichedOthers = othSrv.map(o => {
        const full = getStudentFull(o.id) || {};
        return {
          ...o,
          default_teacher_id: full.default_teacher_id ?? o.default_teacher_id ?? null,
        };
      });
      if (!reqId || reqId === fetchRef.current) {
        setOtherEligible(enrichedOthers);
      }
    } catch (e) {
      if (!reqId || reqId === fetchRef.current) {
        setEligibleStudents([]);
        setOtherEligible([]);
      }
    }
  }

  /** ---------- Modal open ---------- */
  async function openModalFor(date, seg, e) {
    if (!currentTeacherId) { setStatus('Teacher not detected.'); return; }
    const track = trackRefs.current[date];
    if (!track) return;
    if (isPastDate(date)) { setStatus('Cannot add on a past day.'); return; }

    const rect = track.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;

    const segFromM = toMinutes(seg.from);
    let segToM   = toMinutes(seg.to);

    if (segToM - segFromM < SLOT_MIN) segToM = segFromM + SLOT_MIN;

    const segLeftPx  = rect.left  + ((segFromM - dayStartM) / spanM) * rect.width;
    const segRightPx = rect.left  + ((segToM   - dayStartM) / spanM) * rect.width;
    const segWidthPx = Math.max(1, segRightPx - segLeftPx);
    const localPx    = Math.max(0, Math.min(segWidthPx, clientX - segLeftPx));
    const segSpanM   = segToM - segFromM;

    let minuteAtX =
      segFromM + Math.round((localPx / segWidthPx) * (segSpanM / SNAP_MIN)) * SNAP_MIN;
    minuteAtX = Math.max(segFromM, Math.min(segToM - SLOT_MIN, minuteAtX));

    const start = minutesToHHMM(minuteAtX);
    const defaultEnd = minutesToHHMM(Math.min(minuteAtX + SLOT_MIN, segToM));

    setModalDate(date);
    setModalFrom(start);
    setModalMaxTo(seg.to);
    setIsEvent(false);
    setEventEndTime(defaultEnd);
    setModalAnchor({ from: seg.from, to: seg.to });

    const reqId = ++fetchRef.current;
    setLoadingOther(true);

    await computeEligibility({
      date,
      from: start,
      to: defaultEnd,
      type: 'standard',
      reqId,
    });

    if (fetchRef.current === reqId) {
      setShowModal(true);
      setLoadingOther(false);
    }
  }

  /** ---------- Recompute eligibility on modal changes ---------- */
  useEffect(() => {
    if (!showModal) return;
    const end = isEvent
      ? (eventEndTime || modalFrom)
      : minutesToHHMM(
          Math.min(toMinutes(modalFrom) + SLOT_MIN, toMinutes(modalMaxTo))
        );
    const typeStr = isEvent ? 'event' : 'standard';

    const reqId = ++fetchRef.current;
    setLoadingOther(true);
    computeEligibility({
      date: modalDate,
      from: modalFrom,
      to: end,
      type: typeStr,
      reqId,
    }).finally(() => {
      if (fetchRef.current === reqId) setLoadingOther(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEvent, eventEndTime, distanceMode, sortOthersByDistance]);

  // Add local candidate
  function addCandidate(date, from, to, student, slotType='standard', anchor=null) {
    if (!canAddVisitForStudent(date, student.id)) {
      setStatus('Cannot add: this student already has a visit on this day.');
      return;
    }

    const full = getStudentFull(student.id) || student;

    setCandidatesByDate(prev => {
      const cp = { ...prev };
      const list = (cp[date] || []).slice();
      list.push({
        from,
        to,
        student: {
          id: full.id,
          name: (full.last_name || '') + ', ' + (full.first_name || ''),
          default_teacher_id: full.default_teacher_id ?? null
        },
        type: slotType,
        event_end_time: slotType === 'event' ? to : null,
        readonly: false,
        anchor: anchor || { from: DAY_START, to: DAY_END }
      });
      cp[date] = list;
      return cp;
    });
  }

  // Select student in modal
  async function selectStudent(s) {
    const end = isEvent
      ? normalizeTime(eventEndTime)
      : minutesToHHMM(
          Math.min(toMinutes(modalFrom) + SLOT_MIN, toMinutes(modalMaxTo))
        );

    const { freeByDate } = await ensureStudentWeek(s.id);
    const sFree = freeByDate[modalDate] || [];
    const covering = sFree.find(x =>
      toMinutes(modalFrom) >= toMinutes(x.from) &&
      toMinutes(end)       <= toMinutes(x.to)
    );
    const studentSeg = covering || { from: modalFrom, to: end };

    // Anchor = teacher segment ∩ student segment
    const aFrom = minutesToHHMM(
      Math.max(toMinutes(modalAnchor.from), toMinutes(studentSeg.from))
    );
    const aTo = minutesToHHMM(
      Math.min(toMinutes(modalAnchor.to),   toMinutes(studentSeg.to))
    );

    addCandidate(
      modalDate,
      modalFrom,
      end,
      s,
      isEvent ? 'event' : 'standard',
      { from: aFrom, to: aTo }
    );
    setShowModal(false);
  }

  // Delete saved visit
  async function deleteSavedVisit(date, idx) {
    const busy = (teacherBusyByDate[date] || []);
    const item = busy[idx];
    if (!item) return;
    if (item.status === 'complete') {
      setStatus('Cannot delete completed visit.');
      return;
    }
    const ok = window.confirm('Delete this visit?');
    if (!ok) return;
    try {
      await API.delete(`/visits/${item.id}`);
      await refreshAll();
      setStatus('Visit deleted.');
    } catch {
      setStatus('Delete failed.');
    }
  }

  // Delete local candidate
  function deleteCandidate(date, idx) {
    setCandidatesByDate(prev => {
      const cp = { ...prev };
      const list = (cp[date] || []).slice();
      list.splice(idx, 1);
      cp[date] = list;
      return cp;
    });
  }

  // Save all candidates – koristi novu /api/visits/bulk logiku (teacher role)
  async function handleSaveAll() {
    if (!currentTeacherId) {
      setStatus('Teacher not detected.');
      return;
    }

    const payload = [];
    const skipped = [];

    // već postojeće posete ovog teachera (po danu i studentu)
    const used = new Set();
    for (const { date } of weekDates) {
      const busy = (teacherBusyByDate[date] || []);
      for (const v of busy) {
        if (v.status === 'canceled') continue;
        used.add(`${date}|${v.student_id}`);
      }
    }

    for (const { date } of weekDates) {
      if (isPastDate(date)) continue;
      const list = (candidatesByDate[date] || []);

      for (const c of list) {
        const key = `${date}|${c.student.id}`;
        if (used.has(key)) {
          skipped.push({
            date,
            student: c.student.name,
            from: c.from,
            to: c.to
          });
          continue;
        }
        used.add(key);

        const studentDefaultTid =
          c.student.default_teacher_id ??
          getStudentDefaultTid(c.student.id) ??
          null;

        const isDefaultForThisTeacher =
          String(studentDefaultTid || '') === String(currentTeacherId);

        const original_tid = isDefaultForThisTeacher
          ? Number(currentTeacherId)
          : (studentDefaultTid ? Number(studentDefaultTid) : Number(currentTeacherId));

        const reassigned_status = isDefaultForThisTeacher
          ? 'none'
          : (studentDefaultTid ? 'pending' : 'none');

        payload.push({
          student_id: c.student.id,
          teacher_id: Number(currentTeacherId),
          scheduled_at: `${date} ${normalizeTime(c.from)}`,
          notes: '',
          visit_id: null,
          status: 'draft',
          type: c.type || 'standard',
          event_end_time: normalizeTime(c.to),
          original_teacher_id: original_tid,
          reassigned_status
        });
      }
    }

    if (!payload.length) {
      if (skipped.length) {
        setStatus('Nothing to save (all candidates skipped by per-day limits).');
      } else {
        setStatus('Nothing to save.');
      }
      return;
    }

    try {
      const res = await API.post('/visits/bulk', { visits: payload });
      if (!res.data?.success) {
        setStatus('Save failed.');
        return;
      }

      if (skipped.length) {
        setStatus(
          `Saved ${payload.length}; skipped ${skipped.length} due to per-day limits (1 visit per student per day).`
        );
      } else {
        setStatus('Saved.');
      }

      setCandidatesByDate({});
      await refreshAll();
    } catch (e) {
      setStatus(e.response?.data?.error || 'Save failed.');
    }
  }

  async function refreshAll() {
    if (currentTeacherId && selectedWeekStart) {
      try {
        const res = await API.get(
          `/availability/week/${selectedWeekStart}?teacher_id=${currentTeacherId}`
        );
        const a = res.data?.availability || {};
        const e = res.data?.exceptions || {};
        const daysOff = res.data?.day_offs || [];

        const base = {};
        Object.keys(a).forEach(date => {
          const isDayOff = daysOff.includes(date);
          const free = isDayOff ? [] : subtractIntervals(a[date], e[date] || []);
          base[date] = { free };
        });

        const b = await API.get('/visits/teacher-week-all', {
          params: { teacher_id: currentTeacherId, week_start: selectedWeekStart }
        });
        const busy = {};
        (Object.entries(b.data || {})).forEach(([date, list]) => {
          busy[date] = (list || []).map(r => ({
            id: r.id,
            from: normalizeTime(r.from_time),
            to: normalizeTime(r.to_time),
            student_id: r.student_id,
            student_name: r.student_name,
            type: r.type || 'standard',
            status: r.status || 'draft',
            reassigned_status: r.reassigned_status || 'none'
          }));
        });

        Object.keys(base).forEach(date => {
          const free = base[date].free || [];
          const blocked = (busy[date] || []).map(x => ({ from: x.from, to: x.to }));
          base[date].free = subtractIntervals(free, blocked);
        });

        setTeacherBusyByDate(busy);
        setTeacherAvailByDate(base);
      } catch {
        // ignore
      }
    }
  }

  /** ---------- Drag & Resize za kandidate ---------- */
  function startDrag(e, date, idx, mode) {
    const track = trackRefs.current[date];
    if (!track) return;
    const item = (candidatesByDate[date] || [])[idx];
    if (!item) return;

    const rect = track.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    e.preventDefault();
    setDrag({
      date,
      idx,
      mode,
      startX: clientX,
      rectLeft: rect.left,
      rectWidth: rect.width,
      origFrom: item.from,
      origTo: item.to,
      isEvent: item.type === 'event'
    });
  }

  function onMove(e) {
    if (!drag) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const dx = clientX - drag.rectLeft;
    const minuteAtX = dayStartM +
      Math.round((dx / drag.rectWidth) * spanM / SNAP_MIN) * SNAP_MIN;

    setCandidatesByDate(prev => {
      const cp = { ...prev };
      const list = (cp[drag.date] || []).slice();
      const item = { ...list[drag.idx] };
      if (!item) return prev;

      const origDur = toMinutes(drag.origTo) - toMinutes(drag.origFrom);
      const minDur  = drag.isEvent ? SNAP_MIN : SLOT_MIN;
      let newFrom = toMinutes(item.from);
      let newTo   = toMinutes(item.to);

      if (drag.mode === 'move') {
        const anchorAtStart = dayStartM +
          Math.round(
            ((drag.startX - drag.rectLeft) / drag.rectWidth) *
            spanM / SNAP_MIN
          ) * SNAP_MIN;
        const anchorOffset = toMinutes(drag.origFrom) - anchorAtStart;
        newFrom = minuteAtX + anchorOffset;
        newTo   = newFrom + origDur;
      } else if (drag.mode === 'resize-left') {
        const maxLeft = toMinutes(item.to) - minDur;
        newFrom = Math.min(maxLeft, minuteAtX);
      } else if (drag.mode === 'resize-right') {
        const minRight = toMinutes(item.from) + minDur;
        newTo = Math.max(minRight, minuteAtX);
      }

      // day bounds
      newFrom = Math.max(dayStartM, Math.min(newFrom, dayEndM - minDur));
      newTo   = Math.max(newFrom + minDur, Math.min(newTo, dayEndM));

      if (isPastDate(drag.date)) return prev;

      // clamp to anchor
      const aFrom = toMinutes(item.anchor?.from ?? DAY_START);
      const aTo   = toMinutes(item.anchor?.to   ?? DAY_END);

      if (aTo - aFrom < (drag.isEvent ? Math.min(minDur, origDur) : origDur)) {
        return prev;
      }

      if (drag.mode === 'move') {
        newFrom = Math.max(aFrom, Math.min(aTo - origDur, newFrom));
        newTo   = newFrom + origDur;
      } else {
        newFrom = Math.max(aFrom, newFrom);
        newTo   = Math.min(aTo,   newTo);
        if (newTo - newFrom < minDur) {
          if (drag.mode === 'resize-left') {
            newFrom = Math.max(aFrom, newTo - minDur);
          } else {
            newTo = Math.min(aTo, newFrom + minDur);
          }
        }
        if (!drag.isEvent && (newTo - newFrom > MAX_STANDARD_MIN)) {
          if (drag.mode === 'resize-left') {
            newFrom = newTo - MAX_STANDARD_MIN;
          } else {
            newTo = newFrom + MAX_STANDARD_MIN;
          }
        }
      }

      item.from = minutesToHHMM(newFrom);
      item.to   = minutesToHHMM(newTo);
      item.event_end_time = item.type === 'event' ? item.to : null;

      list[drag.idx] = item;
      cp[drag.date] = list;
      return cp;
    });
  }

  function endDrag() { setDrag(null); }

  useEffect(() => {
    function up(){ endDrag(); }
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up, { passive: false });
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: false });
    return () => {
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);

  /** ---------- UI ---------- */
  return (
    <div className="p-2 max-w-7xl mx-auto">
      <Toast text={status} onClose={()=>setStatus('')} />

      <h2 className="text-lg font-bold mb-4">My Day Planner (1 visit per student per day)</h2>

      <div className="flex flex-col lg:flex-row gap-4 items-center lg:items-end mb-4">
        <div>
          <label className="block mb-1">Teacher:</label>
          <div className="border px-2 py-1 w-64 rounded bg-gray-50">
            {currentTeacherName || 'My schedule'}
          </div>
        </div>

        {!!currentTeacherId && (
          <>
            <div>
              <label className="block mb-1">Week:</label>
              <select
                className="border px-2 py-1 w-56"
                value={selectedWeekStart}
                onChange={e => setSelectedWeekStart(e.target.value)}
              >
                <option value="">-- Choose week --</option>
                {weekOptions.map((w,i)=>
                  <option key={i} value={w.value}>{w.label}</option>
                )}
              </select>
            </div>

            <div>
              <label className="block mb-1">
                Default student
                <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[11px] align-middle">
                  overlap
                </span>
              </label>

              <select
                className="border px-2 py-1 w-64"
                value={selectedStudentId}
                onChange={async e => {
                  const v = e.target.value;
                  setSelectedStudentId(v);
                  if (v) await ensureStudentWeek(Number(v));
                }}
              >
                <option value="">-- Choose student --</option>
                {defaultStudents
                  .slice()
                  .sort((a,b)=>
                    (a.last_name||'').localeCompare(b.last_name||'') ||
                    (a.first_name||'').localeCompare(b.first_name||'')
                  )
                  .map(s => (
                    <option key={s.id} value={s.id}>
                      {(s.last_name||'')+', '+(s.first_name||'')}
                    </option>
                  ))}
              </select>
            </div>
          </>
        )}
      </div>

      {!!currentTeacherId && !!selectedWeekStart && (
        <>
          <div className="flex items-center gap-4 text-sm mb-3">
            <span className="flex items-center gap-2">
              <span className="inline-block w-5 h-3 rounded bg-blue-300" /> Teacher free
            </span>
            <span className="flex items-center gap-2">
              <span className="inline-block w-5 h-3 rounded bg-teal-400" /> Intersection with selected student
            </span>
            <span className="flex items-center gap-2">
              <span className="inline-block w-5 h-3 rounded bg-gray-300 border" /> Candidate
            </span>
          </div>

          <div className="space-y-4">
            {weekDates.map(({date, dayName}) => {
              const tFree = (teacherAvailByDate[date]?.free || [])
                .map(i => clampTo(i, dayStartM, dayEndM))
                .filter(Boolean);
              const inter = selectedStudentId
                ? (intersectionByDate[date] || [])
                : [];
              const busy = teacherBusyByDate[date] || [];
              const cands = candidatesByDate[date] || [];
              const isPastDay = isPastDate(date);

              return (
                <div key={date} className="border rounded p-3 shadow-sm">
                  <div className={`font-semibold mb-2 px-2 py-1 rounded flex items-center gap-2 ${
                    isPastDay
                      ? 'text-gray-400 bg-gray-50'
                      : 'text-gray-800 bg-gray-100'
                  }`}>
                    <span>{dayName} – {date}</span>
                    {!!busy.length && (
                      <span className="text-xs px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                        Saved {busy.length}
                      </span>
                    )}
                    {!!cands.length && (
                      <span className="text-xs px-1.5 py-0.5 rounded border bg-gray-50 text-gray-700 border-gray-200">
                        Candidates {cands.length}
                      </span>
                    )}
                  </div>

                  <div
                    className="relative h-16 rounded bg-gray-100 border select-none overflow-visible"
                    ref={el => (trackRefs.current[date] = el)}
                  >
                    {/* hourly ticks */}
                    {Array.from({length: (spanM/60)+1}).map((_,i)=>{
                      const leftPct = (i*60)/spanM*100;
                      return (
                        <div key={i}>
                          <div
                            className="absolute top-4 bottom-0 border-l border-gray-300"
                            style={{ left: `${leftPct}%` }}
                          />
                          <div
                            className="absolute top-0 text-[10px] text-gray-500 -translate-x-1/2"
                            style={{ left: `${leftPct}%` }}
                          >
                            {minutesToHHMM(dayStartM + i*60).slice(0,2)}
                          </div>
                        </div>
                      );
                    })}

                    {/* teacher free */}
                    {tFree.map((seg,idx)=>{
                      const box = pctBox(seg);
                      const clickable = !isPastDay;
                      return clickable ? (
                        <button
                          key={'t'+idx}
                          className="absolute top-4 bottom-1 bg-blue-300 opacity-40 hover:opacity-50 rounded"
                          style={{left:box.left,width:box.width}}
                          title={`Teacher free ${seg.from}–${seg.to}`}
                          onClick={(e)=>openModalFor(date, seg, e)}
                        />
                      ) : (
                        <div
                          key={'t'+idx}
                          className="absolute top-4 bottom-1 bg-blue-300 opacity-30 rounded"
                          style={{left:box.left,width:box.width}}
                        />
                      );
                    })}

                    {/* intersection with selected student */}
                    {inter.map((seg,idx)=>{
                      const box = pctBox(seg);
                      const clickable = !isPastDay;
                      return clickable ? (
                        <button
                          key={'i'+idx}
                          className="absolute top-4 bottom-1 bg-teal-400/70 hover:bg-teal-400 rounded"
                          style={{left:box.left,width:box.width}}
                          title={`Intersection ${seg.from}–${seg.to}`}
                          onClick={(e)=>openModalFor(date, seg, e)}
                        />
                      ) : (
                        <div
                          key={'i'+idx}
                          className="absolute top-4 bottom-1 bg-teal-400/50 rounded"
                          style={{left:box.left,width:box.width}}
                        />
                      );
                    })}

                    {/* saved visits */}
                    {busy.map((s, idx)=>{
                      const box = pctBox(s);
                      return (
                        <div
                          key={'b'+idx}
                          className={`absolute top-5 bottom-2 rounded border ${
                            s.status==='complete'
                              ? 'border-gray-400 bg-gray-100'
                              : 'border-emerald-700 bg-white'
                          } shadow-sm overflow-hidden`}
                          style={{left:box.left,width:box.width}}
                        >
                          <div className={`h-full ${
                            s.status==='complete'
                              ? 'bg-gray-300'
                              : colorForId(s.student_id)+' opacity-40'
                          }`} />
                          <div className="absolute inset-0 flex items-center justify-between px-2 text-xs">
                            <span className="font-mono">{s.from}–{s.to}</span>
                            <span className="hidden sm:inline font-semibold">
                              {s.student_name || '—'}
                            </span>
                            <span className="sm:hidden font-semibold">
                              {initials(s.student_name || '')}
                            </span>
                            <TypeBadge type={s.type} />
                            <StatusBadge status={s.status} />
                            <ReassignedBadge value={s.reassigned_status} />
                          </div>

                          {(!isPastDay && s.status!=='complete') && (
                            <button
                              className="absolute -top-2 -right-2 bg-white border border-gray-300 text-red-600 rounded-full w-5 h-5 text-xs leading-5"
                              title="Delete"
                              onClick={() => deleteSavedVisit(date, idx)}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      );
                    })}

                    {/* candidates */}
                    {cands.map((c, idx)=>{
                      const box = pctBox(c);
                      return (
                        <div
                          key={'c'+idx}
                          className="absolute top-5 bottom-2 rounded border border-gray-400 bg-white shadow-sm overflow-hidden"
                          style={{left:box.left,width:box.width}}
                        >
                          <div className={`${colorForId(c.student.id)} h-full opacity-40`} />
                          <div className="absolute inset-0 flex items-center justify-between px-2 text-xs">
                            <span className="font-mono">{c.from}–{c.to}</span>
                            <span className="hidden sm:inline font-semibold">
                              {c.student.name || '—'}
                            </span>
                            <span className="sm:hidden font-semibold">
                              {initials(c.student.name || '')}
                            </span>
                            <TypeBadge type={c.type} />
                          </div>

                          {!isPastDay && (
                            <>
                              <div
                                className="absolute inset-0 cursor-grab"
                                onMouseDown={(e)=>startDrag(e,date,idx,'move')}
                                onTouchStart={(e)=>startDrag(e,date,idx,'move')}
                              />
                              <div
                                className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize"
                                onMouseDown={(e)=>startDrag(e,date,idx,'resize-left')}
                                onTouchStart={(e)=>startDrag(e,date,idx,'resize-left')}
                              />
                              <div
                                className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize"
                                onMouseDown={(e)=>startDrag(e,date,idx,'resize-right')}
                                onTouchStart={(e)=>startDrag(e,date,idx,'resize-right')}
                              />
                            </>
                          )}

                          <button
                            className="absolute -top-2 -right-2 bg-white border border-gray-300 text-red-600 rounded-full w-5 h-5 text-xs leading-5"
                            title="Remove"
                            onClick={() => deleteCandidate(date, idx)}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {(busy.length > 0 || cands.length > 0) && (
                    <div className="mt-2 space-y-1">
                      {busy.map((s, idx)=>(
                        <div key={'bl'+idx} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-block w-3 h-3 rounded-full ${colorForId(s.student_id)}`} />
                            <span className="font-semibold">
                              {s.student_name || '—'}
                            </span>
                            <span className="font-mono">{s.from}–{s.to}</span>
                            <TypeBadge type={s.type} />
                            <StatusBadge status={s.status} />
                            <ReassignedBadge value={s.reassigned_status} />
                            {s.status === 'complete' && (
                              <span className="text-gray-600 text-xs">
                                Visit completed
                              </span>
                            )}
                          </div>
                          {s.status!=='complete' && !isPastDay ? (
                            <button
                              className="px-2 py-0.5 rounded border text-red-600"
                              onClick={()=>deleteSavedVisit(date, idx)}
                            >
                              Delete
                            </button>
                          ) : (
                            <span className="px-2 py-0.5 rounded border bg-gray-50 text-gray-500 cursor-not-allowed">
                              Locked
                            </span>
                          )}
                        </div>
                      ))}
                      {cands.map((c, idx)=>(
                        <div key={'cl'+idx} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-block w-3 h-3 rounded-full ${colorForId(c.student.id)}`} />
                            <span className="font-semibold">
                              {c.student.name || '—'}
                            </span>
                            <span className="font-mono">{c.from}–{c.to}</span>
                            <TypeBadge type={c.type} />
                            <span className="text-xs text-gray-600">candidate</span>
                          </div>
                          <button
                            className="px-2 py-0.5 rounded border text-red-600"
                            onClick={()=>deleteCandidate(date, idx)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-6">
            <button
              onClick={handleSaveAll}
              className="px-4 py-2 rounded text-white bg-blue-600 hover:bg-blue-700"
            >
              Save all candidates
            </button>
          </div>
        </>
      )}

      {/* Modal: Select Student */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded max-w-xl w-full relative max-h-[80vh] overflow-y-auto">
            <button
              className="absolute top-2 right-2 text-red-500 text-xl"
              onClick={()=>setShowModal(false)}
            >
              ×
            </button>
            <h3 className="text-lg font-bold mb-2">Select Student</h3>
            <div className="text-sm text-gray-600 mb-2">
              Date: <b>{modalDate}</b> | Start: <b>{modalFrom}</b>
            </div>

            {/* Distance controls */}
            <div className="flex flex-wrap items-center gap-3 mb-3 bg-gray-50 rounded p-2">
              <label className="text-sm">
                Distance mode:{' '}
                <select
                  className="border px-2 py-1 text-sm"
                  value={distanceMode}
                  onChange={e => setDistanceMode(e.target.value)}
                >
                  <option value="none">None</option>
                  <option value="haversine">Straight (Haversine)</option>
                  <option value="ors">Route (ORS)</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sortOthersByDistance}
                  onChange={e => setSortOthersByDistance(e.target.checked)}
                />
                <span>Sort others by distance</span>
              </label>
            </div>

            {/* Event toggle */}
		<label className="flex items-center gap-2 mt-2 mb-2">
		  <input
		    type="checkbox"
		    checked={isEvent}
		    onChange={e => {
		      setIsEvent(e.target.checked);
		      if (!e.target.checked) setEventEndTime('');
		    }}
		  />

		  <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
		    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
		      PI
		    </span>
			<span className="text-xs sm:text-sm">or</span>
		    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">
		      Event visit (multi-hour)
		    </span>
		  </div>
		</label>

            <div className="flex items-center gap-2 mb-3">
              <label>End time:</label>
              <input
                type="time"
                value={isEvent
                  ? eventEndTime
                  : minutesToHHMM(
                      Math.min(
                        toMinutes(modalFrom) + SLOT_MIN,
                        toMinutes(modalMaxTo)
                      )
                    )
                }
                min={modalFrom}
                max={modalMaxTo}
                onChange={e => setEventEndTime(e.target.value)}
                className="border px-2 py-1"
                disabled={!isEvent}
              />
            </div>

            {/* Default students (eligible/unavailable) */}
            {eligibleStudents.length ? (
              eligibleStudents.map(row => (
                <div
                  key={row.student.id}
                  className={`mb-2 border p-2 rounded ${
                    row.ok ? 'bg-green-50' : 'bg-yellow-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block w-3 h-3 rounded-full ${colorForId(row.student.id)}`}
                        />
                        <div className="font-semibold">
                          {(row.student.last_name||'')+', '+(row.student.first_name||'')}
                          <PrevRefPill prev_ref={row.student.prev_ref} />
                        </div>
                      </div>
                      <DistanceLine item={row.student} />
                    </div>
                    <button
                      className={`px-2 py-0.5 rounded ${
                        row.ok
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                      }`}
                      disabled={!row.ok}
                      onClick={() => row.ok && selectStudent(row.student)}
                    >
                      {row.ok ? 'Add' : 'Unavailable'}
                    </button>
                  </div>
                  {!row.ok && row.reason && (
                    <div className="text-xs text-gray-700 mt-1">
                      {row.reason}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-700">
                No eligible default students for this time window.
              </div>
            )}

            <hr className="my-3" />
            <div className="text-sm font-semibold mb-2">
              Other students (available now)
            </div>
            {loadingOther ? (
              <div className="text-xs text-gray-500">Loading…</div>
            ) : otherEligible.length ? (
              otherEligible.map(s => (
                <div
                  key={s.id}
                  className="mb-2 border p-2 rounded bg-blue-50"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block w-3 h-3 rounded-full ${colorForId(s.id)}`}
                        />
                        <div className="font-semibold">
                          {(s.last_name||'')+', '+(s.first_name||'')}
                          <PrevRefPill prev_ref={s.prev_ref} />
                        </div>
                      </div>
                      <DistanceLine item={s} />
                    </div>
                    <button
                      className="px-2 py-0.5 rounded bg-blue-600 text-white"
                      onClick={() => selectStudent(s)}
                    >
                      Add
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-xs text-gray-600">
                No other available students for this window.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
